import { useMemo } from 'react';
import type { IFContratoResumo } from '../types/financiamento.types';

// ═══════════════════════════════════════════════════════════════════════════════
// Triagem Fiduciária — Ranking de Intervenção Prioritária
// ═══════════════════════════════════════════════════════════════════════════════

export interface IFTriagemScoreBreakdown {
  probAtrasoRaw: number;
  probAtrasoContrib: number;
  scoreRiscoRaw: number;
  scoreRiscoContrib: number;
  inerciaRaw: number;
  inerciaContrib: number;
  tendenciaRaw: number;
  tendenciaContrib: number;
}

export interface IFTriagemItem {
  contratoId: string;
  nome: string;
  contratada: string;
  urgencyScore: number;
  capitalExposto: number;
  impactoMin: number;
  impactoMax: number;
  recomendacao: string;
  severidade: 'critico' | 'atencao' | 'monitorar';
  prazoAcao: string;
  drivers: string[];
  breakdown: IFTriagemScoreBreakdown;
  justificativas: string[];
  consequencia: string;
  confianca: 'alta' | 'media' | 'baixa';
  alertasAtivos?: number;
}

/** Normalize inertia: 0d → 0, ≥60d → 100 */
function normInertia(periodoFim: string | null): number {
  if (!periodoFim) return 100;
  const days = Math.floor((Date.now() - new Date(periodoFim).getTime()) / 86_400_000);
  if (days <= 0) return 0;
  if (days >= 60) return 100;
  return (days / 60) * 100;
}

/** Normalize trend to 0-100 */
function normTrend(tendencia: string | null): number {
  if (tendencia === 'piorando') return 100;
  if (tendencia === 'melhorando') return 0;
  return 50;
}

/** Human-readable inertia */
function inertiaDays(periodoFim: string | null): number | null {
  if (!periodoFim) return null;
  const days = Math.floor((Date.now() - new Date(periodoFim).getTime()) / 86_400_000);
  return days >= 0 ? days : 0;
}

/** Risk factor multiplier */
function riskFactor(scoreRisco: number | null): number {
  const s = scoreRisco ?? 0;
  if (s >= 60) return 1.0;
  if (s >= 40) return 0.8;
  return 0.6;
}

/** Confidence based on data availability */
function calcConfianca(r: IFContratoResumo): 'alta' | 'media' | 'baixa' {
  let signals = 0;
  if (r.prob_atraso != null) signals++;
  if (r.score_risco != null) signals++;
  if (r.ultimo_boletim_periodo_fim != null) signals++;
  if (r.tendencia != null) signals++;
  if (signals >= 3) return 'alta';
  if (signals >= 2) return 'media';
  return 'baixa';
}

const TREND_LABELS: Record<string, string> = {
  piorando: 'Tendência piorando',
  melhorando: 'Tendência melhorando',
  estavel: 'Tendência estável',
};

export interface IFTriagemOneLiner {
  text: string;
  criticalCount: number;
  totalImpactoMax: number;
}

export function useIFTriagem(resumos: IFContratoResumo[], alertasPorContrato?: Map<string, number>): { items: IFTriagemItem[]; oneLiner: IFTriagemOneLiner | null } {
  return useMemo(() => {
    if (resumos.length === 0) return { items: [], oneLiner: null };

    const totalCarteira = resumos.reduce((s, r) => s + r.valor_global, 0);
    if (totalCarteira === 0) return { items: [], oneLiner: null };

    const items: IFTriagemItem[] = resumos.map((r) => {
      const probAtraso = (r.prob_atraso ?? 0) * 100;
      const scoreRisco = r.score_risco ?? 0;
      const inerciaNorm = normInertia(r.ultimo_boletim_periodo_fim);
      const tendenciaNorm = normTrend(r.tendencia);

      // Contributions
      const probAtrasoContrib = probAtraso * 0.35;
      const scoreRiscoContrib = scoreRisco * 0.25;
      const inerciaContrib = inerciaNorm * 0.20;
      const tendenciaContrib = tendenciaNorm * 0.20;

      const urgencyRaw = probAtrasoContrib + scoreRiscoContrib + inerciaContrib + tendenciaContrib;

      // Capital-weighted urgency
      const capitalWeight = r.valor_global / totalCarteira;
      const urgencyScore = Math.min(100, urgencyRaw * (1 + capitalWeight));

      // Financial impact with risk factor range
      const rf = riskFactor(r.score_risco);
      const baseImpact = r.valor_global * (r.prob_atraso ?? 0);
      const impactoMin = baseImpact * Math.max(0.6, rf - 0.2);
      const impactoMax = baseImpact * Math.min(1.0, rf + 0.2);

      // Build drivers (TOP contributors sorted by magnitude)
      const driverEntries: { label: string; value: number }[] = [];
      const days = inertiaDays(r.ultimo_boletim_periodo_fim);

      if (probAtraso > 0) driverEntries.push({ label: `Prob. atraso ${probAtraso.toFixed(0)}%`, value: probAtrasoContrib });
      if (scoreRisco > 0) driverEntries.push({ label: `Score risco ${scoreRisco.toFixed(0)}`, value: scoreRiscoContrib });
      if (days !== null && days > 0) driverEntries.push({ label: `Inércia ${days}d`, value: inerciaContrib });
      else if (days === null) driverEntries.push({ label: 'Sem medição', value: inerciaContrib });
      if (r.tendencia === 'piorando') driverEntries.push({ label: TREND_LABELS.piorando, value: tendenciaContrib });
      if (capitalWeight > 0.15) driverEntries.push({ label: `${(capitalWeight * 100).toFixed(0)}% da carteira`, value: capitalWeight * 10 });

      driverEntries.sort((a, b) => b.value - a.value);
      const drivers = driverEntries.slice(0, 3).map(d => d.label);

      // Justificativas (human-readable explanations)
      const justificativas: string[] = [];
      if (probAtraso > 50) justificativas.push(`Probabilidade de atraso: ${probAtraso.toFixed(0)}%`);
      if (r.tendencia === 'piorando') justificativas.push('Tendência negativa nas últimas medições');
      if (days !== null && days > 30) justificativas.push(`${days} dias sem atualização de medição`);
      else if (days === null) justificativas.push('Nenhuma medição registrada');
      if (scoreRisco >= 60) justificativas.push(`Score de risco operacional: ${scoreRisco.toFixed(0)}/100`);
      if (capitalWeight > 0.15) justificativas.push(`Concentra ${(capitalWeight * 100).toFixed(0)}% do capital da carteira`);

      // Recommendation rules (stricter: urgency > 70 AND capital > 5%)
      let recomendacao: string;
      let severidade: 'critico' | 'atencao' | 'monitorar';
      let prazoAcao: string;
      let consequencia: string;

      if (urgencyScore > 70 && capitalWeight > 0.05) {
        recomendacao = 'Exige reunião imediata com a contratada — avaliar step-in ou plano de recuperação';
        severidade = 'critico';
        prazoAcao = 'Imediato (≤ 5 dias)';
        consequencia = `Risco de impacto de até ${fmtBRLCompact(impactoMax)} nas próximas 6 semanas`;
      } else if (urgencyScore > 70) {
        recomendacao = 'Solicitar relatório detalhado e agendar auditoria presencial';
        severidade = 'critico';
        prazoAcao = 'Imediato (≤ 5 dias)';
        consequencia = `Exposição de até ${fmtBRLCompact(impactoMax)} sem intervenção`;
      } else if (urgencyScore >= 50) {
        recomendacao = 'Auditar último boletim e verificar consistência físico-financeira';
        severidade = 'atencao';
        prazoAcao = 'Próximo ciclo (≤ 15 dias)';
        consequencia = `Possível desvio financeiro de ${fmtBRLCompact(impactoMin)} a ${fmtBRLCompact(impactoMax)}`;
      } else {
        recomendacao = 'Monitoramento padrão — sem ação necessária';
        severidade = 'monitorar';
        prazoAcao = 'Próximo relatório';
        consequencia = 'Sem risco imediato identificado';
      }

      return {
        contratoId: r.contrato_id,
        nome: r.contrato_nome,
        contratada: r.contratada,
        urgencyScore,
        capitalExposto: r.valor_global,
        impactoMin,
        impactoMax,
        recomendacao,
        severidade,
        prazoAcao,
        drivers,
        breakdown: {
          probAtrasoRaw: probAtraso,
          probAtrasoContrib,
          scoreRiscoRaw: scoreRisco,
          scoreRiscoContrib,
          inerciaRaw: inerciaNorm,
          inerciaContrib,
          tendenciaRaw: tendenciaNorm,
          tendenciaContrib,
        },
        justificativas,
        consequencia,
        confianca: calcConfianca(r),
        alertasAtivos: alertasPorContrato?.get(r.contrato_id) ?? 0,
      };
    });

    // Sort by financial impact (highest max first), then urgency
    items.sort((a, b) => b.impactoMax - a.impactoMax || b.urgencyScore - a.urgencyScore);

    // One-liner executivo
    const criticals = items.filter(i => i.severidade === 'critico');
    const oneLiner: IFTriagemOneLiner | null = criticals.length > 0
      ? {
          text: `${criticals.length} contrato${criticals.length > 1 ? 's' : ''} representa${criticals.length > 1 ? 'm' : ''} até ${fmtBRLCompact(criticals.reduce((s, i) => s + i.impactoMax, 0))} em risco imediato — ação recomendada em até 5 dias.`,
          criticalCount: criticals.length,
          totalImpactoMax: criticals.reduce((s, i) => s + i.impactoMax, 0),
        }
      : null;

    return { items, oneLiner };
  }, [resumos, alertasPorContrato]);
}

/** Compact BRL formatter for use inside the hook */
function fmtBRLCompact(v: number): string {
  if (v >= 1e9) return `R$ ${(v / 1e9).toFixed(1).replace('.', ',')}B`;
  if (v >= 1e6) return `R$ ${(v / 1e6).toFixed(1).replace('.', ',')}M`;
  if (v >= 1e3) return `R$ ${(v / 1e3).toFixed(0)}K`;
  return `R$ ${v.toFixed(0)}`;
}
